import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { ReservationRequest, ReservationStatus } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  CalendarCheck,
  Phone,
  Mail,
  Users,
  CheckCircle2,
  Clock,
  XCircle,
  MessageCircle,
  DollarSign,
  Search,
  Filter,
  Trash2,
} from 'lucide-react';

export const ReservationList: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite } = useSite();
  const [reservations, setReservations] = useState<ReservationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const loadReservations = async () => {
    if (!activeSite) return;
    setLoading(true);
    const data = await dbService.getReservations(activeSite.id);
    setReservations(data);
    setLoading(false);
  };

  useEffect(() => {
    loadReservations();
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Rezervasyon Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Müşteri rezervasyon taleplerini incelemek, onaylamak veya WhatsApp üzerinden yanıtlamak için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleStatusChange = async (resId: string, newStatus: ReservationStatus) => {
    await dbService.updateReservationStatus(activeSite.id, resId, newStatus);
    loadReservations();
  };

  const handleDelete = async (resId: string) => {
    if (confirm('Bu rezervasyon kaydını silmek istediğinizden emin misiniz?')) {
      await dbService.deleteReservation(activeSite.id, resId);
      loadReservations();
    }
  };

  const filteredReservations = reservations.filter((r) => {
    const matchesStatus = statusFilter === 'ALL' || r.status === statusFilter;
    const q = (searchQuery || '').toLowerCase();
    const matchesSearch =
      (r.customerName ? r.customerName.toLowerCase().includes(q) : false) ||
      (r.customerEmail ? r.customerEmail.toLowerCase().includes(q) : false) ||
      (r.customerPhone ? r.customerPhone.includes(searchQuery) : false) ||
      (r.yachtName ? r.yachtName.toLowerCase().includes(q) : false);
    return matchesStatus && matchesSearch;
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-600">
            <CalendarCheck className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Yat Kiralama Portalı</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Gelen Rezervasyon & Kiralama Talepleri
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Müşterilerinizin web siteniz üzerinden gönderdiği mavi tur ve yat kiralama taleplerini yönetin.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-xl bg-cyan-50 text-cyan-700 text-xs font-bold border border-cyan-200">
            Toplam {reservations.length} Talep
          </span>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Müşteri adı, telefon veya yat ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {[
            { key: 'ALL', label: 'Tümü' },
            { key: 'PENDING', label: '⏳ Bekleyen' },
            { key: 'CONTACTED', label: '📞 İletişime Geçildi' },
            { key: 'CONFIRMED', label: '✓ Onaylandı' },
            { key: 'COMPLETED', label: '🎉 Tamamlandı' },
            { key: 'CANCELLED', label: '✕ İptal' },
          ].map((status) => (
            <button
              key={status.key}
              onClick={() => setStatusFilter(status.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                statusFilter === status.key
                  ? 'bg-cyan-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Reservations List */}
      {loading ? (
        <div className="text-center py-12 text-xs text-slate-400">Talepler yükleniyor...</div>
      ) : filteredReservations.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 space-y-3">
          <CalendarCheck className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-bold text-sm text-slate-700">Rezervasyon Talebi Bulunamadı</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            Web sitenizden yapılan rezervasyon form başvuruları anında burada listelenecektir.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Card View (md:hidden) */}
          <div className="md:hidden space-y-3.5">
            {filteredReservations.map((res) => {
              const statusColors: Record<ReservationStatus, string> = {
                PENDING: 'bg-amber-100 text-amber-800 border-amber-300',
                CONTACTED: 'bg-blue-100 text-blue-800 border-blue-300',
                CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                CANCELLED: 'bg-rose-100 text-rose-800 border-rose-300',
                COMPLETED: 'bg-purple-100 text-purple-800 border-purple-300',
              };

              return (
                <div
                  key={res.id}
                  className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-sm text-slate-900">{res.customerName}</div>
                      <div className="text-xs font-semibold text-cyan-600 mt-0.5">
                        {res.yachtName || 'Genel Mavi Tur'}
                      </div>
                    </div>
                    <select
                      value={res.status}
                      onChange={(e) => handleStatusChange(res.id, e.target.value as ReservationStatus)}
                      className={`text-[11px] font-bold px-2 py-1 rounded-lg border outline-none cursor-pointer ${
                        statusColors[res.status]
                      }`}
                    >
                      <option value="PENDING">⏳ Bekleyen</option>
                      <option value="CONTACTED">📞 İletişimde</option>
                      <option value="CONFIRMED">✓ Onaylandı</option>
                      <option value="COMPLETED">🎉 Tamamlandı</option>
                      <option value="CANCELLED">✕ İptal</option>
                    </select>
                  </div>

                  {/* Dates & Price bar */}
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-xl text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 block font-medium">Tarihler ({res.guestCount} Kişi)</span>
                      <span className="font-mono text-slate-700 text-[11px] font-semibold">
                        {res.startDate} → {res.endDate}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-slate-400 block font-medium">Tahmini Tutar</span>
                      <span className="font-bold text-slate-900 text-xs">
                        {res.totalEstimatedPrice?.toLocaleString('tr-TR')} {res.currency}
                      </span>
                    </div>
                  </div>

                  {res.notes && (
                    <p className="text-xs text-slate-500 italic bg-amber-50/60 border border-amber-100 p-2 rounded-lg">
                      "{res.notes}"
                    </p>
                  )}

                  {/* Quick Action Buttons */}
                  <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                    <a
                      href={`tel:${res.customerPhone}`}
                      className="flex-1 py-2 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold flex items-center justify-center gap-1.5 transition"
                    >
                      <Phone className="w-3.5 h-3.5 text-slate-600" />
                      <span>Ara</span>
                    </a>
                    <a
                      href={`https://wa.me/${res.customerPhone.replace(/[^0-9]/g, '')}?text=Merhaba%20${encodeURIComponent(
                        res.customerName
                      )},%20${encodeURIComponent(activeSite.name)}%20uzerinden%20yaptiginiz%20rezervasyon%20talebi%20icin%20ulastik.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 py-2 px-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow-sm"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span>WhatsApp</span>
                    </a>
                    <button
                      onClick={() => handleDelete(res.id)}
                      className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                      title="Sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View (hidden on mobile) */}
          <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3.5 px-4">Müşteri</th>
                    <th className="py-3.5 px-4">İletişim</th>
                    <th className="py-3.5 px-4">Talep Edilen Yat</th>
                    <th className="py-3.5 px-4">Tarih Aralığı</th>
                    <th className="py-3.5 px-4">Kişi</th>
                    <th className="py-3.5 px-4">Tahmini Tutar</th>
                    <th className="py-3.5 px-4">Durum</th>
                    <th className="py-3.5 px-4 text-right">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredReservations.map((res) => {
                    const statusColors: Record<ReservationStatus, string> = {
                      PENDING: 'bg-amber-100 text-amber-800 border-amber-300',
                      CONTACTED: 'bg-blue-100 text-blue-800 border-blue-300',
                      CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                      CANCELLED: 'bg-rose-100 text-rose-800 border-rose-300',
                      COMPLETED: 'bg-purple-100 text-purple-800 border-purple-300',
                    };

                    return (
                      <tr key={res.id} className="hover:bg-slate-50/70 transition">
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{res.customerName}</div>
                          {res.notes && <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">"{res.notes}"</div>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-0.5">
                            <a
                              href={`tel:${res.customerPhone}`}
                              className="text-slate-700 hover:text-cyan-600 flex items-center gap-1 font-medium"
                            >
                              <Phone className="w-3 h-3 text-slate-400" />
                              {res.customerPhone}
                            </a>
                            <a
                              href={`mailto:${res.customerEmail}`}
                              className="text-slate-500 hover:text-cyan-600 flex items-center gap-1 text-[11px]"
                            >
                              <Mail className="w-3 h-3 text-slate-400" />
                              {res.customerEmail}
                            </a>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-semibold text-slate-800">
                          {res.yachtName || 'Genel Mavi Tur'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-mono text-[11px] text-slate-700">
                            {res.startDate} → {res.endDate}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-700 font-semibold">{res.guestCount} Kişi</td>
                        <td className="py-3 px-4 font-bold text-slate-900">
                          {res.totalEstimatedPrice?.toLocaleString('tr-TR')} {res.currency}
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={res.status}
                            onChange={(e) => handleStatusChange(res.id, e.target.value as ReservationStatus)}
                            className={`text-[11px] font-bold px-2 py-1 rounded-lg border outline-none cursor-pointer ${
                              statusColors[res.status]
                            }`}
                          >
                            <option value="PENDING">Bekleyen (Yeni)</option>
                            <option value="CONTACTED">İletişime Geçildi</option>
                            <option value="CONFIRMED">Onaylandı</option>
                            <option value="COMPLETED">Tamamlandı</option>
                            <option value="CANCELLED">İptal Edildi</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <a
                              href={`https://wa.me/${res.customerPhone.replace(/[^0-9]/g, '')}?text=Merhaba%20${encodeURIComponent(
                                res.customerName
                              )},%20${encodeURIComponent(activeSite.name)}%20uzerinden%20yaptiginiz%20rezervasyon%20talebi%20icin%20ulastik.`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition"
                              title="WhatsApp İle Mesaj At"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => handleDelete(res.id)}
                              className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                              title="Sil"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
